import React from 'react';
import { AvailableRoomModel, AvailableRoomPlan, AvailableRoomType, HotelInfoModel } from '@/__generated';
import RoomDetail from './RoomDetail';
import RoomFilters from './RoomFilters';
import { IRoomFilters, ISelectedRoomTypes } from '../type';
import { cloneDeep } from 'lodash';
import { getCurrentSearchQuery, getHotelDetailRequest } from '@/urls';
import { BoardType } from '@/constants';

type Props = {
  hotelInfo: HotelInfoModel;
  availableRooms: AvailableRoomModel[];
  selectedRooms: ISelectedRoomTypes[];
  onSelectedRoomChange: (
    noOfAdult: number,
    noOfChild: number,
    roomType: AvailableRoomType,
    plan: AvailableRoomPlan,
    selectedRoomNumber: number,
  ) => void;
};

const AvailableRooms = ({ hotelInfo, availableRooms, onSelectedRoomChange, selectedRooms }: Props) => {
  const [boardTypeFilters, setBoardTypeFilters] = React.useState<string[]>([]);
  const [roomTypeFilters, setRoomTypeFilters] = React.useState<string[]>([]);
  const [roomProviderFilter, setRoomProviderFilter] = React.useState<string | undefined>(getCurrentSearchQuery('provider') || undefined);
  const [ratePlanFilter, setRatePlanFilter] = React.useState<string | undefined>(getCurrentSearchQuery('campaignRatePlan') || undefined);

  React.useEffect(() => {
    if (availableRooms?.length) {
      const roomRequest = getHotelDetailRequest(window.location.search);
      setBoardTypeFilters(roomRequest?.boardType ? [BoardType.__getValue(roomRequest.boardType)] : []);
      setRoomTypeFilters([]);
    }
  }, [availableRooms]);

  const roomFilters = React.useMemo(() => {
    if (availableRooms?.length) {
      let roomTypesDict = {};
      let boardTypesDict = {};
      let providerDict = {};
      availableRooms.forEach(availableRoom => {
        availableRoom?.availableRoomTypes?.forEach(room => {
          if (roomTypesDict[room.category || '']) {
            roomTypesDict[room.category || ''] += room.roomPlans?.length;
          } else {
            roomTypesDict[room.category || ''] = room.roomPlans?.length;
          }

          room.roomPlans?.forEach(plan => {
            if (boardTypesDict[plan.boardType || '']) {
              boardTypesDict[plan.boardType || ''] += 1;
            } else {
              boardTypesDict[plan.boardType || ''] = 1;
            }
            if (providerDict[room.supplier || '']) {
              providerDict[room.supplier || ''] += 1;
            } else {
              providerDict[room.supplier || ''] = 1;
            }
          });
        });
      });
      let boardTypes = Object.keys(boardTypesDict).map(k => ({ key: k, label: k, count: boardTypesDict[k] }));
      const roomRequest = getHotelDetailRequest(window.location.search);
      if (roomRequest?.boardType) {
        if (!boardTypes.some(x => x.key === BoardType.__getValue(roomRequest.boardType))) {
          boardTypes = [
            {
              count: 0,
              key: BoardType.__getValue(roomRequest.boardType),
              label: BoardType.__getValue(roomRequest.boardType),
            },
            ...boardTypes,
          ];
        }
      }

      return {
        boardTypes: boardTypes,
        roomTypes: Object.keys(roomTypesDict).map(k => ({ key: k, label: k, count: roomTypesDict[k] })),
        roomProviders: Object.keys(providerDict).map(k => ({ key: k, label: k, count: providerDict[k] })),
      } as IRoomFilters;
    }
    return undefined;
  }, [availableRooms]);

  const onBoardTypeFilterChange = (k: string) => {
    setBoardTypeFilters(types => {
      if (types.includes(k)) {
        return types.filter(x => x !== k);
      }
      return [...types, k];
    });
  };
  const onRoomTypeFilterChange = (k: string) => {
    setRoomTypeFilters(types => {
      if (types.includes(k)) {
        return types.filter(x => x !== k);
      }
      return [...types, k];
    });
  };
  const clearAllFilter = () => {
    setRoomTypeFilters([]);
    setBoardTypeFilters([]);
    setRoomProviderFilter(undefined);
    setRatePlanFilter(undefined);
  };
  const availableRoomsWithFilter = React.useMemo(() => {
    let availableRoomsClones = cloneDeep(availableRooms);
    let rooms: AvailableRoomModel[] = [];
    // room refundable filter;
    availableRoomsClones.forEach(availableRooms => {
      let availableRoomTypes = availableRooms.availableRoomTypes?.filter(roomType => roomType.roomPlans?.some(plan => !plan.nonRefundable));
      if (availableRoomTypes?.length) {
        rooms.push({
          ...availableRooms,
          availableRoomTypes: availableRoomTypes.map(roomType => ({
            ...roomType,
            roomPlans: roomType.roomPlans?.filter(plan => !plan.nonRefundable),
          })),
        });
      }
    });

    // filter with room type
    if (roomTypeFilters?.length) {
      let roomWittRoomTypeFilter: AvailableRoomModel[] = [];
      rooms.forEach(availableRooms => {
        let availableRoomTypes = availableRooms.availableRoomTypes?.filter(roomType => roomTypeFilters.includes(roomType.category || ''));
        if (availableRoomTypes?.length) {
          roomWittRoomTypeFilter.push({
            ...availableRooms,
            availableRoomTypes: availableRoomTypes,
          });
        }
      });
      rooms = roomWittRoomTypeFilter;
    }

    // filter with room provider
    if (roomProviderFilter === 'nta') {
      let roomsWithProviderFilter: AvailableRoomModel[] = [];
      rooms.forEach(availableRooms => {
        let availableRoomTypes = availableRooms.availableRoomTypes?.filter(roomType => roomType.supplier === 'NTA');
        if (availableRoomTypes?.length) {
          roomsWithProviderFilter.push({
            ...availableRooms,
            availableRoomTypes: availableRoomTypes,
          });
        }
      });
      rooms = roomsWithProviderFilter;
    }
    // rate plan filter
    if (ratePlanFilter) {
      let planCodes = ratePlanFilter.split(',');

      let roomsWithRatePlanFilter: AvailableRoomModel[] = [];
      rooms.forEach(availableRooms => {
        let availableRoomTypes = availableRooms.availableRoomTypes?.filter(roomType =>
          roomType.roomPlans?.some(plan => planCodes.includes(plan.code || '')),
        );
        if (availableRoomTypes?.length) {
          roomsWithRatePlanFilter.push({
            ...availableRooms,
            availableRoomTypes: availableRoomTypes.map(roomType => ({
              ...roomType,
              roomPlans: roomType.roomPlans?.filter(plan => planCodes.includes(plan.code || '')),
            })),
          });
        }
      });
      rooms = roomsWithRatePlanFilter;
    }
    // board type filter
    if (boardTypeFilters?.length) {
      let roomsForBoardTypeFilters: AvailableRoomModel[] = [];
      rooms.forEach(availableRooms => {
        let availableRoomTypes = availableRooms.availableRoomTypes?.filter(roomType =>
          roomType.roomPlans?.some(plan => boardTypeFilters.includes(plan.boardType || '')),
        );
        if (availableRoomTypes?.length) {
          roomsForBoardTypeFilters.push({
            ...availableRooms,
            availableRoomTypes: availableRoomTypes.map(roomType => ({
              ...roomType,
              roomPlans: roomType.roomPlans?.filter(plan => boardTypeFilters.includes(plan.boardType || '')),
            })),
          });
        }
      });
      rooms = roomsForBoardTypeFilters;
    }

    // return rooms
    return rooms;
  }, [availableRooms, boardTypeFilters, roomTypeFilters, roomProviderFilter, ratePlanFilter]);

  const hasFilter = React.useMemo(() => {
    return boardTypeFilters.length || roomTypeFilters.length || roomProviderFilter === 'nta' || !!ratePlanFilter;
  }, [boardTypeFilters, roomTypeFilters, roomProviderFilter, ratePlanFilter]);
  return (
    <>
      <RoomFilters
        roomFilters={roomFilters}
        availableRooms={availableRooms}
        onBoardTypeFilterChange={onBoardTypeFilterChange}
        onRoomTypeFilterChange={onRoomTypeFilterChange}
        selectedBoardTypes={boardTypeFilters}
        selectedRoomTypes={roomTypeFilters}
        clearAllFilter={clearAllFilter}
        roomProviderFilter={roomProviderFilter}
        onRoomProviderFilterChange={f => setRoomProviderFilter(f)}
        ratePlanFilter={ratePlanFilter}
        onRatePlanFilterChange={f => setRatePlanFilter(f)}
      />
      {availableRoomsWithFilter?.map(room => {
        return (
          <div className="rounded mb-30" key={`${room.noOfAdult}-${room.noOfChild}`} id={`room-category-${room.noOfAdult}-${room.noOfChild}`}>
            <div className="bg-dark-3 rounded-top-2 text-white text-20 fw-500  px-10 py-10">
              {`#Room: ${room.noOfAdult} adult`}
              {`${room.noOfChild ? ' - ' + room.noOfChild + ' children' : ''} `}
            </div>
            {room.availableRoomTypes?.map((availableRoom, index) => {
              return (
                <RoomDetail
                  selectedRooms={selectedRooms}
                  noOfAdult={room.noOfAdult || 0}
                  noOfChild={room.noOfChild || 0}
                  onSelectedRoomChange={onSelectedRoomChange}
                  hotelInfo={hotelInfo}
                  availableRoomType={availableRoom}
                  key={`${availableRoom.roomTypeId}-${index}`}
                />
              );
            })}

            {/* End .row */}
          </div>
        );
      })}
      {availableRoomsWithFilter?.length === 0 && hasFilter ? (
        <section className="pt-40 border-light rounded-4" style={{ backgroundColor: '#f0f8ff' }}>
          <div className="text-center">
            <img className="pb-30 " src="/static/icons/search-hotel.svg" alt="search hotel" style={{ width: 100 }} />
            <div className="fw-500 text-18 pb-30 ">
              Unfortunately, all rate plans matching your selected conditions are sold out.
              <br /> You may find alternative options by modifying the <b>Quick Search</b> criteria or selecting a different date.
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default AvailableRooms;
